import React, { useEffect, useState } from 'react';
import { Layout, Nav, Toast, Modal, SideSheet, Button } from '@douyinfe/semi-ui';
import { IconSetting, IconUser, IconHistogram, IconLayers, IconPriceTag, IconLock, IconAlignLeft, IconCustomerSupport } from '@douyinfe/semi-icons';
import { API } from '../helpers';
import { useHashRoute, navigate } from '../router';
import { useSite } from '../context/SiteContext';
import AdminTicketsBell from '../components/AdminTicketsNavButton';

const { Header, Sider, Content } = Layout;

const NAV_ITEMS = [
  { itemKey: 'stats',    text: '网关总览', icon: <IconHistogram /> },
  { itemKey: 'channels', text: '渠道管理', icon: <IconLayers /> },
  { itemKey: 'tokens',    text: '令牌管理', icon: <IconUser /> },
  { itemKey: 'plans',    text: '套餐管理', icon: <IconPriceTag /> },
  { itemKey: 'tickets',  text: '工单管理', icon: <IconCustomerSupport /> },
  { itemKey: 'settings', text: '系统设置', icon: <IconSetting /> },
];

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

const AdminLayout = ({ children }) => {
  const path = useHashRoute();
  const { site } = useSite();
  const [checking, setChecking] = useState(true);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // 受控字段:避开 Semi Form 的 useForm(useFormApi 在 2.x 里不能挂在 Form 上)
  const [pwd, setPwd] = useState({ old: '', neu: '', confirm: '' });

  const isMobile = useIsMobile();

  // 检查登录态:任何非成功响应都跳回登录页
  useEffect(() => {
    API.get('/api/admin/me')
      .then((r) => { if (!r.data.success) navigate('/admin/login'); })
      .catch(() => navigate('/admin/login'))
      .finally(() => setChecking(false));
  }, []);

  const handleLogout = async () => {
    await API.post('/api/admin/logout');
    Toast.success('已登出');
    navigate('/admin/login');
  };

  const openPwd = () => {
    setPwd({ old: '', neu: '', confirm: '' });
    setPwdOpen(true);
  };
  const closePwd = () => {
    setPwdOpen(false);
  };

  // 提交改密:成功后后端会清空所有 session 并清 cookie,前端需跳回登录页
  const handleChangePassword = async () => {
    if (!pwd.old || !pwd.neu || !pwd.confirm) {
      Toast.error('请填写完整三项');
      return;
    }
    if (pwd.neu !== pwd.confirm) {
      Toast.error('两次输入的新密码不一致');
      return;
    }
    if (pwd.neu.length < 8) {
      Toast.error('新密码至少 8 位');
      return;
    }
    setPwdSaving(true);
    try {
      await API.post('/api/admin/password', {
        old_password: pwd.old,
        new_password: pwd.neu,
      });
      Toast.success('密码已修改,请用新密码重新登录');
      setPwdOpen(false);
      // 后端已清 cookie,直接跳登录页
      setTimeout(() => navigate('/admin/login'), 600);
    } catch (e) {
      Toast.error(e.response?.data?.message || '修改失败');
    } finally {
      setPwdSaving(false);
    }
  };

  if (checking) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#86909c' }}>加载中…</div>;
  }
  // 登录态由 useEffect 处理:这里不再依赖 me

  const selectedKey = (() => {
    if (path.startsWith('/admin/stats'))     return 'stats';
    if (path.startsWith('/admin/channels'))  return 'channels';
    if (path.startsWith('/admin/tokens'))     return 'tokens';
    if (path.startsWith('/admin/plans'))      return 'plans';
    if (path.startsWith('/admin/tickets'))    return 'tickets';
    if (path.startsWith('/admin/settings'))   return 'settings';
    return 'stats';
  })();

  const handleNavSelect = (k) => {
    if (k.itemKey === 'stats')    navigate('/admin/stats');
    if (k.itemKey === 'channels') navigate('/admin/channels');
    if (k.itemKey === 'tokens')    navigate('/admin/tokens');
    if (k.itemKey === 'plans')    navigate('/admin/plans');
    if (k.itemKey === 'tickets')  navigate('/admin/tickets');
    if (k.itemKey === 'settings') navigate('/admin/settings');
    if (isMobile) setDrawerOpen(false);
  };

  // 通用行样式
  const labelStyle = { display: 'block', fontSize: 13, color: '#4e5969', marginBottom: 6 };
  const inputStyle = {
    width: '100%', height: 32, padding: '0 10px',
    border: '1px solid #e5e6eb', borderRadius: 4,
    fontSize: 14, outline: 'none', boxSizing: 'border-box',
  };

  const navEl = (
    <Nav
      selectedKeys={[selectedKey]}
      style={{ height: '100%', width: '100%' }}
      onSelect={handleNavSelect}
      items={NAV_ITEMS}
    />
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        padding: isMobile ? '0 12px' : '0 24px',
        height: 56,
        lineHeight: '56px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <a href="/" style={{ color: '#18181b', display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 10, textDecoration: 'none', cursor: 'pointer' }}>
          <img src="/logo.svg" alt="" style={{ width: 28, height: 28 }} />
          <strong style={{ fontSize: 16, fontWeight: 600 }}>{isMobile ? site.site_name : `${site.site_name} · 管理后台`}</strong>
        </a>
        {!isMobile ? (
          <div style={{ color: '#52525b', fontSize: 13, display: 'flex', alignItems: 'center', gap: 16 }}>
            <AdminTicketsBell />
            <a
              href="#password"
              onClick={(e) => { e.preventDefault(); openPwd(); }}
              style={{ color: '#52525b', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <IconLock style={{ marginRight: 4, verticalAlign: -2 }} />修改密码
            </a>
            <a
              href="#logout"
              onClick={(e) => { e.preventDefault(); handleLogout(); }}
              style={{ color: '#52525b', textDecoration: 'none' }}
            >
              退出
            </a>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Button
              icon={<IconAlignLeft />}
              theme="borderless"
              type="tertiary"
              style={{ fontSize: 20, padding: '0 4px' }}
              onClick={() => setDrawerOpen(true)}
              aria-label="菜单"
            />
          </div>
        )}
      </Header>
      <Layout>
        {!isMobile && (
          <Sider style={{ background: '#fff' }} width={200}>
            {navEl}
          </Sider>
        )}
        <Content style={{ padding: isMobile ? 12 : 20, background: '#f7f8fa' }}>
          {children}
        </Content>
      </Layout>

      {/* 移动端抽屉菜单 */}
      <SideSheet
        title="管理菜单"
        visible={drawerOpen}
        onCancel={() => setDrawerOpen(false)}
        width={240}
        placement="right"
        showClose={true}
        footer={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ padding: '6px 0', display: 'flex', alignItems: 'center' }}>
              <AdminTicketsBell />
            </div>
            <a
              href="#password"
              onClick={(e) => { e.preventDefault(); setDrawerOpen(false); openPwd(); }}
              style={{ color: '#52525b', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '6px 0' }}
            >
              <IconLock /> 修改密码
            </a>
            <a
              href="#logout"
              onClick={(e) => { e.preventDefault(); handleLogout(); }}
              style={{ color: '#f56565', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '6px 0' }}
            >
              退出登录
            </a>
          </div>
        }
      >
        {navEl}
      </SideSheet>

      {/* 修改密码弹窗(用受控 input 避开 Semi Form 的 useForm 兼容问题) */}
      <Modal
        title="修改管理员密码"
        visible={pwdOpen}
        onCancel={closePwd}
        confirmLoading={pwdSaving}
        onOk={handleChangePassword}
        okText="确认修改"
        cancelText="取消"
        width={420}
      >
        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>当前密码</label>
            <input
              type="password"
              style={inputStyle}
              value={pwd.old}
              onChange={(e) => setPwd({ ...pwd, old: e.target.value })}
              placeholder="请输入当前密码"
              autoFocus
            />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>新密码 <span style={{ color: '#c9cdd4', fontWeight: 'normal' }}>(至少 8 位)</span></label>
            <input
              type="password"
              style={inputStyle}
              value={pwd.neu}
              onChange={(e) => setPwd({ ...pwd, neu: e.target.value })}
              placeholder="请输入新密码"
            />
          </div>
          <div style={{ marginBottom: 6 }}>
            <label style={labelStyle}>确认新密码</label>
            <input
              type="password"
              style={inputStyle}
              value={pwd.confirm}
              onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
              placeholder="再次输入新密码"
              onKeyDown={(e) => { if (e.key === 'Enter') handleChangePassword(); }}
            />
          </div>
          <div className="form-hint">
            修改成功后会自动登出,需用新密码重新登录
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default AdminLayout;
